// components/TokenExpirationNotification.js
import React, { useEffect, useState, useRef } from 'react';
import useTokenMonitor from '../hooks/useTokenMonitor';

const TokenExpirationNotification = () => {
  const { timeLeftFormatted, timeRemaining, refreshToken } = useTokenMonitor();
  const [showFifteenMsg, setShowFifteenMsg] = useState(false);
  const [fifteenShown, setFifteenShown] = useState(false);
  const [showUrgentMsg, setShowUrgentMsg] = useState(true);
  
  // Références pour stocker si l'utilisateur a déjà fermé chaque type de message
  const hasDismissedUrgent = useRef(false);
  const hasDismissedFifteen = useRef(false);

  // Afficher l'alerte 15 minutes une seule fois (seulement si pas déjà fermée)
  useEffect(() => {
    if (!fifteenShown && !hasDismissedFifteen.current && timeRemaining !== null && timeRemaining <= 15 * 60 * 1000) {
      setShowFifteenMsg(true);
      setFifteenShown(true);

      // Masquer après 5 secondes
      const timer = setTimeout(() => setShowFifteenMsg(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [timeRemaining, fifteenShown]);

  // Gérer l'affichage du message urgent (seulement si pas déjà fermé définitivement)
  useEffect(() => {
    if (!hasDismissedUrgent.current && timeRemaining !== null && timeRemaining <= 8 * 60 * 1000 && timeRemaining > 0) {
      setShowUrgentMsg(true);
    }
  }, [timeRemaining]);

  const handleRefresh = async () => {
    try {
      await refreshToken();
      console.log('Session rafraîchie avec succès');
      // Optionnel: réinitialiser l'état après un refresh réussi
      hasDismissedUrgent.current = false;
      hasDismissedFifteen.current = false;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement:', error);
    }
  };

  const handleDismiss = () => {
    setShowUrgentMsg(false);
    hasDismissedUrgent.current = true; // Marquer comme définitivement fermé
  };

  const handleDismissFifteen = () => {
    setShowFifteenMsg(false);
    hasDismissedFifteen.current = true; // Marquer comme définitivement fermé
  };

  // Réinitialiser les états quand le token est rafraîchi (optionnel)
  useEffect(() => {
    // Si le temps restant est supérieur à 8 minutes, on peut réinitialiser
    if (timeRemaining !== null && timeRemaining > 8 * 60 * 1000) {
      hasDismissedUrgent.current = false;
      hasDismissedFifteen.current = false;
    }
  }, [timeRemaining]);

  // Alerte quand il reste moins de 5 minutes
  if (showUrgentMsg && !hasDismissedUrgent.current && timeRemaining !== null && timeRemaining <= 8 * 60 * 1000 && timeRemaining > 0) {
    return (
      <div
        style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '20px',
          backgroundColor: timeRemaining <= 60 * 1000 ? '#ff4444' : '#ffbb33',
          color: 'white',
          borderRadius: '12px',
          boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
          zIndex: 9999,
          animation: 'slideIn 0.3s ease',
          minWidth: '350px',
          textAlign: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', justifyContent: 'center', marginBottom: '20px' }}>
          <span style={{ fontSize: '36px' }}>
            {timeRemaining <= 60 * 1000 ? '⚠️' : '⏰'}
          </span>
          <div>
            <strong style={{ fontSize: '20px' }}>
              {timeRemaining <= 60 * 1000 ? 'Urgent !' : 'Attention'}
            </strong>
            <div style={{ fontSize: '16px' }}>Votre session expire dans {timeLeftFormatted}</div>
          </div>
        </div>
        
        {/* Boutons d'action */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
          <button
            onClick={handleDismiss}
            style={{
              padding: '10px 25px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid white',
              borderRadius: '25px',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              flex: '1',
              maxWidth: '120px',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
            }}
          >
            OK
          </button>
          
          <button
            onClick={handleRefresh}
            style={{
              padding: '10px 25px',
              backgroundColor: 'white',
              border: 'none',
              borderRadius: '25px',
              color: timeRemaining <= 60 * 1000 ? '#ff4444' : '#ffbb33',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              flex: '1',
              maxWidth: '120px',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

 

  return null;
};

// Ajouter les animations CSS
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
document.head.appendChild(style);

export default TokenExpirationNotification;